import { useEffect, useState } from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateView } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import 'dayjs/locale/fr';
import i18n from '../../../i18n/i18n'
import { deDE, frFR, esES, itIT, nlNL } from "@mui/x-date-pickers/locales";

interface Props {
  label: string;
  value: Dayjs | null;
  onChange: (value: Dayjs | null) => void;
  className?: string;
  views?: DateView[];
  minDate?: Dayjs;
  maxDate?: Dayjs;
}

// Map from languages in MIUI datepickers
const localeTextMap: Record<string, any> = {
  fr: frFR.components.MuiLocalizationProvider.defaultProps.localeText,
  de: deDE.components.MuiLocalizationProvider.defaultProps.localeText,
  es: esES.components.MuiLocalizationProvider.defaultProps.localeText,
  it: itIT.components.MuiLocalizationProvider.defaultProps.localeText,
  nl: nlNL.components.MuiLocalizationProvider.defaultProps.localeText,
};

const loadDayjsLocale = async (lang: string) => {
  switch (lang) {
    case "fr":
      return import("dayjs/locale/fr");
    case "de":
      return import("dayjs/locale/de");
    case "es":
      return import("dayjs/locale/es");
    case "it":
      return import("dayjs/locale/it");
    case "nl":
      return import("dayjs/locale/nl");
    default:
      return import("dayjs/locale/en");
  }
};

const InputDate = ({
  label,
  onChange,
  className,
  value,
  views = ["year", "month"],
  minDate,
  maxDate,
}: Props) => {
  const [localeReady, setLocaleReady] = useState(false);
  const [open, setOpen] = useState(false);
  const currentLang = i18n.language || "en";

  useEffect(() => {
    const load = async () => {
      try {
        await loadDayjsLocale(currentLang);
      } catch (err) {
        console.warn(`error loading locale Dayjs: ${currentLang}`, err);
      }
      setLocaleReady(true);
    };

    load();
  }, [currentLang]);

  const localeText = localeTextMap[currentLang] ?? undefined;

  if (!localeReady) return null;
  
  return (
    <LocalizationProvider 
      dateAdapter={AdapterDayjs}
      adapterLocale={currentLang}
      localeText={localeText}
    >
      <DatePicker
        className={`InputDate ${className}`}
        label={label}
        value={value}
        onChange={onChange}
        openTo="year"
        views={views}
        disableFuture
        format="MM / YYYY"
        minDate={minDate}
        maxDate={maxDate}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        slotProps={{
          textField: {
            onClick: () => setOpen(true),
            onKeyDown: (e) => e.preventDefault(),
            InputProps: {
              readOnly: true,
            },
          },
        }}
      />
    </LocalizationProvider>
  );
};

export default InputDate;
